import { useEffect, useState } from "react";
import { ApiError, type PersonSummary, type ToCheck, api, formatMoney } from "./api";
import { go } from "./layout/router";
import { PageHeader } from "./layout/Layout";
import { Alert, Badge, Box, Empty, Loading, StatCard, Table, Th, TwoLine } from "./ui";

/**
 * What each person holds and is owed (quotation A19), with what is still to be checked
 * (A20) beside it.
 *
 * OHC's people buy stock with their own money and sell it through the company, so at any
 * moment the company is holding somebody's cards and owing somebody money. Adding that up
 * by hand is how it gets forgotten. A person sees their own book; the masters see all of
 * them, and that is decided by the server, not by this screen.
 */
export function People(): React.ReactElement {
  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [waiting, setWaiting] = useState<ToCheck | undefined>();
  const [canSeeAll, setCanSeeAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const [summary, checks] = await Promise.all([api.peopleSummary(), api.checks()]);
        if (!live) return;
        setPeople(summary.people);
        setCanSeeAll(summary.canSeeAll);
        setWaiting(checks.waiting);
      } catch (problem) {
        if (live) setError(problem instanceof ApiError ? problem.message : "Could not load staff balances");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  if (loading) return <Loading />;

  const stockCents = people.reduce((sum, person) => sum + person.stock.costCents, 0);
  const owedCents = people.reduce((sum, person) => sum + person.owed.amountCents, 0);
  const toCheck = waiting ? waiting.purchases + waiting.lots : 0;

  return (
    <>
      <PageHeader
        title={canSeeAll ? "Staff balances" : "My balances"}
        subtitle={
          canSeeAll
            ? "Inventory held by staff for the company, and amounts payable to them."
            : "Inventory you hold for OHC, and amounts OHC owes you."
        }
      />

      {error ? <Alert tone="danger">{error}</Alert> : null}

      <div className="grid grid-cols-12 gap-x-6">
        <div className="xl:col-span-4 md:col-span-6 col-span-12">
          <StatCard
            label={canSeeAll ? "Inventory held by staff - at cost" : "Your inventory - at cost"}
            value={formatMoney(stockCents)}
            icon="ti ti-users"
            tone="primary"
            note={`${people.reduce((sum, person) => sum + person.stock.items, 0)} items in total`}
          />
        </div>
        <div className="xl:col-span-4 md:col-span-6 col-span-12">
          <StatCard
            label={canSeeAll ? "Amount payable to staff" : "Amount payable to you"}
            value={formatMoney(owedCents)}
            icon="ti ti-receipt"
            tone={owedCents > 0 ? "warning" : "success"}
            note={owedCents > 0 ? "On unpaid payment vouchers" : "No outstanding balance"}
            {...(owedCents > 0 ? { noteTone: "warning" as const } : {})}
          />
        </div>
        <div className="xl:col-span-4 md:col-span-6 col-span-12">
          <StatCard
            label="Records awaiting verification"
            value={toCheck}
            icon="ti ti-list-check"
            tone={toCheck > 0 ? "warning" : "success"}
            note={
              waiting
                ? `${waiting.purchases} purchases, ${waiting.lots} batches${waiting.slabsToFinish > 0 ? `, ${waiting.slabsToFinish} incomplete` : ""}`
                : undefined
            }
          />
        </div>
      </div>

      {waiting && (waiting.slabsToFinish > 0 || waiting.buybacksToTurnIntoStock > 0 || toCheck > 0) ? (
        <Box title="Pending tasks">
          <ul className="list-none m-0 p-0 divide-y divide-defaultborder dark:divide-defaultborder/10">
            {waiting.lots > 0 ? (
              <ToDo
                icon="ri-check-double-line"
                what={`${waiting.lots} ${waiting.lots === 1 ? "inventory batch has" : "inventory batches have"} not been verified against the shelf`}
                where="Inventory - awaiting verification"
                onGo={() => go("stock")}
              />
            ) : null}
            {waiting.slabsToFinish > 0 ? (
              <ToDo
                icon="ri-draft-line"
                what={`${waiting.slabsToFinish} ${waiting.slabsToFinish === 1 ? "record was" : "records were"} entered quickly and still need details`}
                where="Inventory - incomplete records"
                onGo={() => go("stock")}
              />
            ) : null}
            {waiting.purchases > 0 ? (
              <ToDo
                icon="ri-shopping-cart-line"
                what={`${waiting.purchases} ${waiting.purchases === 1 ? "supplier purchase has" : "supplier purchases have"} not been verified against the invoice`}
                where="Supplier purchases"
                onGo={() => go("purchases")}
              />
            ) : null}
            {waiting.buybacksToTurnIntoStock > 0 ? (
              <ToDo
                icon="ri-hand-coin-line"
                what={`${waiting.buybacksToTurnIntoStock} ${waiting.buybacksToTurnIntoStock === 1 ? "customer buyback is" : "customer buybacks are"} awaiting inventory entry`}
                where="Sales and buybacks"
                onGo={() => go("sales")}
              />
            ) : null}
          </ul>
        </Box>
      ) : null}

      <Box title={canSeeAll ? "Staff summary" : "Your summary"} count={people.length}>
        {people.length === 0 ? (
          <Empty icon="ri-user-line">No staff records yet.</Empty>
        ) : (
          <Table
            head={
              <>
                <Th>Name</Th>
                <Th end>Inventory held</Th>
                <Th end>Amount payable</Th>
                <Th end>Amount paid</Th>
                <Th>Personal financial accounts</Th>
                <Th>Verification status</Th>
              </>
            }
          >
            {people.map((person) => (
              <tr key={person.personId} className="border-b border-defaultborder">
                <td>
                  <TwoLine main={person.fullName} sub={person.isStaff ? undefined : "Not staff"} />
                </td>
                <td className="!text-end">
                  <TwoLine
                    main={formatMoney(person.stock.costCents)}
                    sub={person.stock.lots > 0 ? `${person.stock.items} items, ${person.stock.lots} batches` : "No inventory held"}
                  />
                </td>
                <td className="!text-end">
                  {person.owed.amountCents > 0 ? (
                    <TwoLine
                      main={<span className="text-warning font-semibold">{formatMoney(person.owed.amountCents)}</span>}
                      sub={`${person.owed.vouchers} ${person.owed.vouchers === 1 ? "voucher" : "vouchers"}`}
                    />
                  ) : (
                    <span className="text-[#8c9097] dark:text-white/50">{formatMoney(0)}</span>
                  )}
                </td>
                <td className="!text-end text-[#8c9097] dark:text-white/50">{formatMoney(person.paidCents)}</td>
                <td>
                  {person.accounts.length === 0 ? (
                    <span className="text-[#8c9097] dark:text-white/50">None recorded</span>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {person.accounts.map((account) => (
                        <span key={account.id} className="text-[0.813rem]">
                          {account.name}
                          <span className="text-[#8c9097] dark:text-white/50"> · {formatMoney(account.balanceCents)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>
                  {person.toCheck > 0 ? (
                    <Badge tone="warning">{person.toCheck}</Badge>
                  ) : (
                    <Badge tone="success">No review required</Badge>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Box>
    </>
  );
}

/** One line of work still waiting, and where it is done. */
function ToDo({
  icon,
  what,
  where,
  onGo,
}: {
  readonly icon: string;
  readonly what: string;
  readonly where: string;
  readonly onGo: () => void;
}): React.ReactElement {
  return (
    <li className="py-3 flex items-center gap-3">
      <span className="avatar avatar-sm avatar-rounded !bg-warning/10 !text-warning !mb-0">
        <i className={`${icon} text-[1rem]`} />
      </span>
      <TwoLine main={what} sub={where} />
      <div className="flex-1" />
      <button type="button" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0" onClick={onGo}>
        Review task <i className="ri-arrow-right-line" />
      </button>
    </li>
  );
}
